// scheduled-push.js — Morning Mode: enqueue/cancel future-dated pushes.
//
// Writes to scheduled_pushes (RLS: owner-only). A per-minute pg_cron job pings
// the drain endpoint (api/push GET), which reads due rows with the service-role
// client and delivers each via web-push. Nothing here holds a secret or sends a
// notification — these helpers only touch the caller's own RLS-protected rows.
//
// The Supabase client is passed in (not imported) so callers hand it the
// Clerk-authed client from their hook/component scope and this stays pure.
// Reads/writes follow the same "throw on error" convention as src/lib/db.js.

// fireAt may be a Date or an ISO/parseable string — normalize to ISO for pg.
function toIso(fireAt) {
  return fireAt instanceof Date ? fireAt.toISOString() : new Date(fireAt).toISOString();
}

async function insertPush(supabase, row) {
  const { data, error } = await supabase
    .from("scheduled_pushes")
    .insert(row)
    .select("id")
    .single();
  if (error) throw error;
  return data?.id ?? null;
}

/**
 * Queue the pet-can-eat push (kind='meds'), fired at meds_given_at + interval.
 * Store the returned id on morning_sessions.meds_push_id so undoing the meds
 * step can cancel it.
 *
 * `pet` comes from preferences.household (see lib/household.js). Copy is built
 * from it rather than hardcoded — this used to read "Mackenzie can eat 🐾" and
 * "since her meds", which is one specific dog and one specific pronoun.
 *
 * @param pet {{name, emoji}|null} — omit and the push falls back to neutral copy
 * @returns {Promise<string|null>} the new scheduled_pushes row id.
 */
// eslint-disable-next-line no-unused-vars -- beanName kept for API symmetry with scheduleRatingPush.
export async function scheduleMedsPush({ supabase, userId, sessionId, beanName, fireAt, pet = null }) {
  const name = pet?.name;
  const emoji = pet?.emoji ? ` ${pet.emoji}` : "";
  return insertPush(supabase, {
    user_id: userId,
    kind: "meds",
    fire_at: toIso(fireAt),
    session_id: sessionId,
    payload: {
      title: name ? `${name} can eat${emoji}` : "Medication timer done",
      // "their" rather than a guessed pronoun — we only ever know the pet's name.
      body: name
        ? `It's been long enough since their meds — food's good to go.`
        : "The medication timer has finished.",
      url: "/#today",
      tag: "meds",
    },
  });
}

/**
 * Queue the post-brew rating prompt (kind='rating'), fired at brewed_at +
 * interval. Deep-links into the rating sheet for this brew.
 * @returns {Promise<string|null>} the new scheduled_pushes row id.
 */
export async function scheduleRatingPush({ supabase, userId, brewLogId, beanName, fireAt }) {
  const bean = beanName || "coffee";
  return insertPush(supabase, {
    user_id: userId,
    kind: "rating",
    fire_at: toIso(fireAt),
    brew_log_id: brewLogId,
    payload: {
      title: "How was the brew?",
      body: `How's the ${bean}? 👍 👎`,
      url: `/#today?rate=${brewLogId}`,
      tag: "rating",
    },
  });
}

/**
 * Cancel a single scheduled push (owner-only via RLS). Used when a step is
 * undone. No-op-safe: canceling an already-sent/canceled row just won't match
 * the pending filter, so it won't fire.
 */
export async function cancelScheduledPush({ supabase, id }) {
  if (!id) return;
  const { error } = await supabase
    .from("scheduled_pushes")
    .update({ status: "canceled" })
    .eq("id", id)
    .eq("status", "pending");
  if (error) throw error;
}

/**
 * Cancel every still-pending push for a morning session — e.g. when the whole
 * session is reset. Only 'pending' rows are touched so already-delivered
 * pushes are left as-is.
 */
export async function cancelPushesForSession({ supabase, sessionId }) {
  if (!sessionId) return;
  const { error } = await supabase
    .from("scheduled_pushes")
    .update({ status: "canceled" })
    .eq("session_id", sessionId)
    .eq("status", "pending");
  if (error) throw error;
}
